import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

export interface ColumnDefinition<T = string> {
  key: T;
  label: string;
  sortable?: boolean;
  defaultVisible?: boolean;
}

interface UseColumnVisibilityOptions<T = string> {
  cookieName: string;
  columns: ColumnDefinition<T>[];
}

export function useColumnVisibility<T extends string = string>({
  cookieName,
  columns,
}: UseColumnVisibilityOptions<T>) {
  // Initialize visibility state from cookies or defaults
  const [visibleColumns, setVisibleColumns] = useState<Set<T>>(() => {
    const savedColumns = Cookies.get(cookieName);
    if (savedColumns) {
      try {
        const parsed = JSON.parse(savedColumns) as T[];
        return new Set(parsed);
      } catch {
        // If parsing fails, use defaults
      }
    }

    // Use default visibility
    return new Set(
      columns
        .filter((col) => col.defaultVisible !== false)
        .map((col) => col.key)
    );
  });

  // Save to cookies whenever visibility changes
  useEffect(() => {
    const columnsArray = Array.from(visibleColumns);
    Cookies.set(cookieName, JSON.stringify(columnsArray), { expires: 365 });
  }, [visibleColumns, cookieName]);

  const toggleColumn = (columnKey: T) => {
    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const isColumnVisible = (columnKey: T) => {
    return visibleColumns.has(columnKey);
  };

  const showAllColumns = () => {
    setVisibleColumns(new Set(columns.map((col) => col.key)));
  };

  const resetToDefaults = () => {
    setVisibleColumns(
      new Set(
        columns
          .filter((col) => col.defaultVisible !== false)
          .map((col) => col.key)
      )
    );
  };

  return {
    visibleColumns,
    isColumnVisible,
    toggleColumn,
    showAllColumns,
    resetToDefaults,
  };
}
